/**
 * **Extend to wall**: carry a chain's free end along its last stretch to the
 * first wall face ahead.
 *
 * A chain splits its room only when both its ends touch a wall (chain.js,
 * `TOUCH_TOL_M`), so one that stops short is shown as a normal internal wall
 * of its room — "doesn't divide it" — with this as the one click that
 * finishes it. The face ahead is whichever the ray from the free end meets
 * first: another structure wall's footprint on the chain's storey, or the
 * room's own outline. The outline counts on its own because a room's boundary
 * runs on wall FACES, and where the scan captured no wall there is still an
 * outline edge for the chain to meet.
 *
 * Pure. X-Z plane; Y is up; increasing Z is screen-down (CLAUDE.md).
 */

const { buildChain, sameStorey, TOUCH_TOL_M } = require('./chain');
const { distanceToPolygonBoundary } = require('./geometry');
const { wallFootprint } = require('./regions');
const { extendStructureWallTo } = require('../room-gap/apply');
const { rayDistanceToPolygon } = require('../geometry/rectifyWalkways');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

/** Past this, nothing ahead is "the wall ahead" — the end points out of the
 *  structure. */
const MAX_EXTEND_M = 10;

const dist = (a, b) => Math.hypot(b.x - a.x, b.z - a.z);

/** A structure wall's footprint as an `{x,z}` ring, ungrown. */
function footprintRing(wall) {
  return wallFootprint(wall, 0).map(([x, z]) => ({ x, z }));
}

/** The chain's two ends, each with the direction its last stretch runs out. */
function chainEnds(chain) {
  const st = chain.stretches;
  const first = st[0];
  const last = st[st.length - 1];
  return [
    { at: first.a, from: first.b, wallId: first.wallId },
    { at: last.b, from: last.a, wallId: last.wallId },
  ];
}

/**
 * Extend to wall, planned: which wall grows, the update that grows it, and by
 * how much.
 *
 * @param {object[]} structureWalls the structure's walls
 * @param {string[]} chainWallIds the chain, as `findChainSplit` reports it
 * @param {{x,z}[]} roomPolygon the room the chain stands in
 * @returns {{ wallId: string, update: object, distance: number } | null}
 *   `update` is `extendStructureWallTo`'s (the shape
 *   `updateStructureWallsGeometry` takes). The nearer of the chain's free ends
 *   to a face ahead; null when neither end is free or nothing is ahead within
 *   10 m.
 */
function planExtendToWall(structureWalls, chainWallIds, roomPolygon) {
  const walls = (structureWalls || []).filter((w) => w?.id);
  const chain = buildChain(walls, chainWallIds);
  if (!chain.ok) return null;
  const outline =
    Array.isArray(roomPolygon) && roomPolygon.length >= 3 ? roomPolygon : null;

  let best = null;
  for (const end of chainEnds(chain)) {
    const endWall = walls.find((w) => w.id === end.wallId);
    const others = walls
      .filter(
        (w) =>
          w.id !== end.wallId &&
          w.center &&
          w.dimensions?.x > 0 &&
          sameStorey(w, endWall)
      )
      .map(footprintRing);
    // An end on the room's outline or on another wall is not free.
    if (outline && distanceToPolygonBoundary(end.at, outline) <= TOUCH_TOL_M) {
      continue;
    }
    if (
      others.some((r) => distanceToPolygonBoundary(end.at, r) <= TOUCH_TOL_M)
    ) {
      continue;
    }

    const len = dist(end.from, end.at);
    if (!(len > 0)) continue;
    const dir = {
      x: (end.at.x - end.from.x) / len,
      z: (end.at.z - end.from.z) / len,
    };
    let ahead = Infinity;
    for (const ring of others) {
      // From inside another wall's body there is no face ahead, only its exit.
      if (isPointInsidePolygon(end.at, ring)) continue;
      const t = rayDistanceToPolygon(end.at, dir, ring);
      if (t != null && t < ahead) ahead = t;
    }
    if (outline) {
      const t = rayDistanceToPolygon(end.at, dir, outline);
      if (t != null && t < ahead) ahead = t;
    }
    if (!(ahead <= MAX_EXTEND_M)) continue;

    const target = { x: end.at.x + dir.x * ahead, z: end.at.z + dir.z * ahead };
    const update = extendStructureWallTo(endWall, target);
    if (!update) continue;
    if (!best || ahead < best.distance) {
      best = { wallId: end.wallId, update, distance: ahead };
    }
  }
  return best;
}

module.exports = { MAX_EXTEND_M, planExtendToWall, footprintRing, chainEnds };
